import React from 'react';
import './ScrollableListCell.scss';

/**
 * Generic scrollable list cell component for displaying comma-separated values
 * @param {object} props - Component props
 * @param {string} props.value - Comma-separated string of values
 * @param {string} [props.separator=', '] - Separator used to split the values
 * @param {number} [props.maxHeight=60] - Maximum height in pixels before scrolling
 * @param {string} [props.className=''] - Additional CSS class names
 * @param {string} [props.itemClassName=''] - Additional CSS class names for individual items
 * @param {boolean} [props.showTooltip=true] - Whether to show tooltip with full content
 * @returns {JSX.Element} Scrollable list cell component
 * 
 * @example
 * // Basic usage for organization names
 * <ScrollableListCell value="Org1, Org2, Org3" />
 * 
 * @example
 * // Custom separator and height
 * <ScrollableListCell 
 *   value="Item1; Item2; Item3" 
 *   separator="; " 
 *   maxHeight={80} 
 * />
 * 
 * @example
 * // With custom styling
 * <ScrollableListCell 
 *   value="Tag1, Tag2, Tag3" 
 *   className="custom-scrollable-cell"
 *   itemClassName="tag-item"
 *   maxHeight={100}
 * />
 */
export const ScrollableListCell = ({ 
    value, 
    separator = ', ', 
    maxHeight = 60, 
    className = '', 
    itemClassName = '', 
    showTooltip = true 
}) => {
    const items = value ? value.split(separator) : [];
    const fullValue = value || '';
    
    // Create CSS custom property for height if different from default
    const customHeightClass = maxHeight !== 60 ? 'custom-height' : '';
    const customHeightStyle = maxHeight !== 60 ? { '--custom-max-height': `${maxHeight}px` } : {};
    
    return (
        <div 
            className={`scrollable-list-cell ${customHeightClass} ${className}`.trim()}
            title={showTooltip ? fullValue : undefined}
            style={customHeightStyle}
        >
            {items.map((item, index) => (
                <div key={index} className={`scrollable-list-item ${itemClassName}`.trim()}>
                    {item.trim()}
                </div>
            ))}
        </div>
    );
}; 